/**
 * Sortable
 *
 * Convenience wrapper for react-sortable-hoc to display a sortable list.
 *
 * Use this in favor of SortableRows.
 */

import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { Props as DndContextProps } from "@dnd-kit/core/dist/components/DndContext/DndContext";
import {
  restrictToHorizontalAxis,
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import React, { useContext } from "react";

import { FormContext } from "../contexts/FormContext";

interface HasChildren {
  children: React.ReactNode;
}

// This Context isn't an awesome way to manage this, but we needed functionality compatibility
// with the old react-sortable-hoc, which did NOT pass listeners down via props.
const DragHandleContext = React.createContext<{
  // eslint-disable-next-line @typescript-eslint/ban-types
  listeners: Record<string, Function> | null | undefined;
  attributes?: Record<string, any>;
}>({ listeners: null });

const SortableElement = ({
  useDragHandle,
  id,
  children,
}: React.PropsWithChildren<{
  id: string;
  useDragHandle?: boolean;
}>) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const realAttributes = useDragHandle
    ? {
        // ...attributes,
        tabIndex: -1,
      }
    : attributes;

  return (
    <DragHandleContext.Provider value={{ listeners, attributes }}>
      <div
        ref={setNodeRef}
        style={style}
        {...realAttributes}
        {...(useDragHandle ? {} : listeners)}
      >
        {children}
      </div>
    </DragHandleContext.Provider>
  );
};

// These props mirror what the old react-sortable-hoc used to expect
type SortableProps = {
  onSortStart?: () => void;
  onSortEnd?: ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => void;
  children: React.ReactNode;

  lockAxis?: "x" | "y";
  useDragHandle?: boolean;
  lockToContainerEdges?: boolean;

  accessibility?: DndContextProps["accessibility"];

  // does not do anything yet
  lockOffset?: [number | string, number | string];
  disableAutoscroll?: boolean;

  disabled?: boolean;
};

const Sortable = (props: SortableProps) => {
  const form = useContext(FormContext);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const items =
    React.Children.map(props.children, (c) => {
      if (!React.isValidElement(c)) {
        if (!c) return undefined;
        throw new TypeError("Invalid child");
      }
      if (!c.key) {
        throw new TypeError("Property key cannot be null");
      }

      return c.key;
    }) ?? [];

  const handleSortEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      props.onSortEnd?.({
        oldIndex: items.indexOf(active.id as string),
        newIndex: items.indexOf(over.id as string),
      });
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleSortEnd}
      onDragStart={props.onSortStart}
      accessibility={props.accessibility}
      modifiers={[
        ...(props.lockAxis === "y" ? [restrictToVerticalAxis] : []),
        ...(props.lockAxis === "x" ? [restrictToHorizontalAxis] : []),
        ...(props.lockToContainerEdges ? [restrictToParentElement] : []),
      ]}
    >
      <SortableContext
        items={items}
        strategy={verticalListSortingStrategy}
        disabled={props.disabled ?? form.disabled}
      >
        {React.Children.map(props.children, (c, idx) => {
          if (!React.isValidElement(c)) return c;
          if (!c.key) {
            throw new TypeError("Property key cannot be null");
          }
          return (
            <SortableElement
              id={c.key}
              key={c.key}
              useDragHandle={props.useDragHandle}
            >
              {c}
            </SortableElement>
          );
        })}
      </SortableContext>
    </DndContext>
  );
};

const DragHandle = ({ children }: Partial<HasChildren>) => {
  const { listeners, attributes } = React.useContext(DragHandleContext);
  return (
    <span {...listeners} {...attributes} tabIndex={0}>
      {children ?? (
        <div className="ui-drag-handle-vertical test-question-handle">
          <div></div>
        </div>
      )}
    </span>
  );
};

Sortable.Handle = React.memo(DragHandle);

export default Sortable;
